import { useEffect, useState } from 'react'
import { YStack, styled } from 'tamagui'

const Container = styled(YStack, {
  name: 'TimeBombContainer',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 0,
})

const CenterGlow = styled(YStack, {
  name: 'CenterGlow',
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: 400,
  height: 400,
  marginLeft: -200,
  marginTop: -100,
  borderRadius: 200,
  opacity: 0.15,
  // @ts-ignore
  background: 'radial-gradient(circle, #FF6300 0%, #FF9D3A 30%, transparent 70%)',
  filter: 'blur(60px)',
})

export function TimeBombVisualizer() {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => (t + 1) % 100)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  return (
    <Container>
      {/* Central glow */}
      <CenterGlow />

      {/* SVG-based animated rings and countdown elements */}
      <svg
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -40%)',
          width: '600px',
          height: '600px',
          opacity: 0.6,
        }}
        viewBox="0 0 600 600"
      >
        <defs>
          {/* Gradient for the rings */}
          <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF6300" stopOpacity="1" />
            <stop offset="50%" stopColor="#FF9D3A" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#FFE0AD" stopOpacity="0.2" />
          </linearGradient>

          {/* Glow filter */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Pulse animation filter */}
          <filter id="pulse" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Outer pulsing ring */}
        <circle
          cx="300"
          cy="300"
          r="250"
          fill="none"
          stroke="url(#ringGradient)"
          strokeWidth="1"
          opacity="0.3"
          style={{
            animation: 'pulseRing 4s ease-in-out infinite',
          }}
        />

        {/* Middle rotating dashed ring */}
        <circle
          cx="300"
          cy="300"
          r="200"
          fill="none"
          stroke="#FF6300"
          strokeWidth="1"
          strokeDasharray="20 10 5 10"
          opacity="0.4"
          filter="url(#glow)"
          style={{
            animation: 'rotateRing 30s linear infinite',
            transformOrigin: '300px 300px',
          }}
        />

        {/* Inner countdown-style segments */}
        {[...Array(12)].map((_, i) => {
          const angle = (i * 30 - 90) * (Math.PI / 180)
          const innerR = 140
          const outerR = 160
          const x1 = 300 + Math.cos(angle) * innerR
          const y1 = 300 + Math.sin(angle) * innerR
          const x2 = 300 + Math.cos(angle) * outerR
          const y2 = 300 + Math.sin(angle) * outerR
          const isActive = i <= (tick % 12)

          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={isActive ? '#FF6300' : '#333'}
              strokeWidth={i % 3 === 0 ? 3 : 2}
              strokeLinecap="round"
              opacity={isActive ? 0.9 : 0.3}
              filter={isActive ? 'url(#glow)' : undefined}
              style={{
                transition: 'opacity 0.3s, stroke 0.3s',
              }}
            />
          )
        })}

        {/* Center core */}
        <circle
          cx="300"
          cy="300"
          r="100"
          fill="none"
          stroke="#FF6300"
          strokeWidth="2"
          opacity="0.2"
        />

        {/* Pulsing core */}
        <circle
          cx="300"
          cy="300"
          r="80"
          fill="none"
          stroke="#FF9D3A"
          strokeWidth="1"
          opacity="0.4"
          style={{
            animation: 'pulseCore 2s ease-in-out infinite',
            transformOrigin: '300px 300px',
          }}
        />

        {/* Animated tick marks around the core */}
        {[...Array(60)].map((_, i) => {
          const angle = (i * 6 - 90) * (Math.PI / 180)
          const r = 120
          const len = i % 5 === 0 ? 8 : 4
          const x1 = 300 + Math.cos(angle) * (r - len)
          const y1 = 300 + Math.sin(angle) * (r - len)
          const x2 = 300 + Math.cos(angle) * r
          const y2 = 300 + Math.sin(angle) * r

          return (
            <line
              key={`tick-${i}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#FF6300"
              strokeWidth={i % 5 === 0 ? 2 : 1}
              opacity={0.3}
            />
          )
        })}

        {/* Sweeping second hand */}
        <line
          x1="300"
          y1="300"
          x2="300"
          y2="190"
          stroke="#FF6300"
          strokeWidth="2"
          strokeLinecap="round"
          filter="url(#glow)"
          style={{
            animation: 'sweepHand 60s linear infinite',
            transformOrigin: '300px 300px',
          }}
        />

        {/* Center dot */}
        <circle
          cx="300"
          cy="300"
          r="6"
          fill="#FF6300"
          filter="url(#glow)"
        />
      </svg>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulseRing {
            0%, 100% {
              transform: scale(1);
              opacity: 0.3;
            }
            50% {
              transform: scale(1.05);
              opacity: 0.5;
            }
          }

          @keyframes rotateRing {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          @keyframes pulseCore {
            0%, 100% {
              transform: scale(1);
              opacity: 0.4;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.6;
            }
          }

          @keyframes sweepHand {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </Container>
  )
}
